import type { SpikeConfig } from '@personal-os/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { fetchSpikeConfig, fetchSpikeStatus, SpikeApiError } from './api';
import { clearStoredToken, readStoredToken } from './storage';
import { TokenGate } from './TokenGate';

export interface SpikeContext {
  token: string;
  config: SpikeConfig;
  lock: () => void;
}

/** Shared front door for the test pages: is the spike on, and is this device unlocked? */
export function SpikeGate({ children }: { children: (ctx: SpikeContext) => ReactNode }) {
  const status = useQuery({ queryKey: ['spike-status'], queryFn: fetchSpikeStatus, retry: 1 });
  const [token, setToken] = useState<string | null>(() => readStoredToken());

  function lock() {
    clearStoredToken();
    setToken(null);
  }

  let body: ReactNode;
  if (status.isPending) {
    body = (
      <section className="card" role="status" data-state="loading">
        <p className="card-title">Loading…</p>
      </section>
    );
  } else if (status.isError) {
    body = (
      <section className="card card-bad" role="status" data-state="error">
        <p className="card-title">Can't reach the server</p>
        <button type="button" onClick={() => void status.refetch()}>
          Try again
        </button>
      </section>
    );
  } else if (!status.data.enabled) {
    body = (
      <section className="card" data-state="disabled">
        <h2 className="card-heading">Not enabled here</h2>
        <p className="card-detail">
          The iPhone test tools are only switched on in the Preview environment.
        </p>
      </section>
    );
  } else if (!token) {
    body = <TokenGate onUnlock={setToken} />;
  } else {
    body = (
      <Unlocked token={token} lock={lock}>
        {children}
      </Unlocked>
    );
  }

  return (
    <main className="page">
      <p className="back">
        <Link to="/">← Home</Link>
      </p>
      {body}
    </main>
  );
}

function Unlocked({
  token,
  lock,
  children,
}: {
  token: string;
  lock: () => void;
  children: (ctx: SpikeContext) => ReactNode;
}) {
  const config = useQuery({
    queryKey: ['spike-config', token],
    queryFn: () => fetchSpikeConfig(token),
    retry: false,
    staleTime: Infinity,
  });

  if (config.isPending) {
    return (
      <section className="card" role="status" data-state="loading">
        <p className="card-title">Unlocking…</p>
      </section>
    );
  }
  if (config.isError) {
    const rejected = config.error instanceof SpikeApiError && config.error.status === 401;
    return (
      <section className="card card-bad" role="alert" data-state="error">
        <p className="card-title">
          {rejected ? 'The saved token was rejected' : "Couldn't load the test settings"}
        </p>
        <button type="button" onClick={rejected ? lock : () => void config.refetch()}>
          {rejected ? 'Enter the token again' : 'Try again'}
        </button>
      </section>
    );
  }
  return <>{children({ token, config: config.data, lock })}</>;
}
